import { type RiskIndicator } from "@/revenue-intelligence/lib/risk-contracts";
import { isWithinInterval, subDays } from "date-fns";
import type {
  StateOfSalesV2ForecastMovement,
  StateOfSalesV2ForecastMovementCategoryKey,
  StateOfSalesV2ForecastMovementDeal,
} from "../lib/risk";

export const FORECAST_MANAGEMENT_ATTENTION_CONFIG = {
  validationStatus: "proposed_for_product_validation",
  maxPriorities: 3,
  dependency: {
    materialShareOfForecast: 0.20,
    criticalShareOfForecast: 0.50,
  },
  urgentDeal: {
    eligibleUrgencyBucket: "high" as const,
    materialShareOfForecast: 0.10,
    criticalShareOfForecast: 0.25,
  },
  concentration: {
    minimumDeals: 2,
    maximumDealsForStageDependencyThesis: 3,
    materialShareOfForecast: 0.50,
    criticalShareOfForecast: 0.75,
    suppressWhenOneDealExplainsShare: 0.80,
  },
  worseningOutlook: {
    materialShareOfForecast: 0.10,
    criticalShareOfForecast: 0.25,
  },
  timingDependency: {
    finalPeriodDays: 14,
    materialShareOfForecast: 0.25,
    criticalShareOfForecast: 0.50,
  },
  forecastAssumption: {
    materialShareOfForecast: 0.25,
    criticalShareOfForecast: 0.50,
    materialRiskIncreasePoints: 10,
    criticalRiskIncreasePoints: 25,
    materialRiskIncreaseDealShare: 0.10,
  },
} as const;

export type ManagementAttentionCandidateType =
  | "forecast_dependency"
  | "urgent_deal"
  | "organisational_concentration"
  | "worsening_outlook"
  | "timing_dependency"
  | "forecast_assumption"
  | "forecast_divergence";

export type ManagementAttentionSeverity = "critical" | "material" | "informational";

export type ManagementAttentionEntityType =
  | "deal"
  | "forecast"
  | "rep"
  | "team"
  | "stage"
  | "pipeline"
  | "segment"
  | "region";

export type ManagementAttentionDeal = {
  adjustedCloseDate?: Date | string | null;
  forecast_contribution?: number | null;
  forecast_share?: number | null;
  id: number | string;
  name?: string | null;
  risk_score?: number | null;
  urgency_bucket?: "high" | "medium" | "low";
  urgency_score?: number | null;
  weighted_risk_score?: number | null;
};

export type ManagementAttentionConcentration<T extends ManagementAttentionDeal> = {
  dealCount: number;
  deals: T[];
  dimension: string;
  key: "pipeline" | "sales_rep" | "stage" | "team";
  label: string;
  share: number;
  value: number;
};

export type ManagementAttentionSignal<T extends ManagementAttentionDeal> = {
  affectedDealCount: number;
  deals: T[];
  forecastShare: number | null;
  forecastValue: number;
  indicator: RiskIndicator;
  label: string;
  discriminativeEvidence?: {
    comparisonRatio?: number;
    kind:
      | "higher_open_baseline"
      | "higher_recent_loss_prevalence"
      | "material_deterioration"
      | "weakened_outlook_relationship";
    movementImpact?: number;
  };
};

export type ManagementRiskThesis = {
  primaryRisk:
    | "target_dependency"
    | "negative_movement"
    | "timing_dependency"
    | "conversion_risk"
    | "progression_risk"
    | "signal_deterioration"
    | "forecast_divergence";
  consequence: {
    forecastWithoutEntity?: number;
    resultingGapToTarget?: number;
    forecastImpact?: number;
    shareOfForecast?: number;
    movementImpact?: number;
  };
  evidence: {
    dealIds: string[];
    riskChange?: number;
    historicalConversion?: number;
    baselineConversion?: number;
    daysInStage?: number;
    normalDaysInStage?: number;
    expectedClose?: string;
    changedIndicators?: string[];
  };
};

export type ManagementAttentionCandidate<
  T extends ManagementAttentionDeal = ManagementAttentionDeal,
> = {
  id: string;
  type: ManagementAttentionCandidateType;
  severity: ManagementAttentionSeverity;
  entityType: ManagementAttentionEntityType;
  entityId?: string;
  dealIds: string[];
  forecastImpact?: number;
  shareOfForecast?: number;
  movementImpact?: number;
  urgencyScore?: number;
  riskChange?: number;
  rootCauseKey: string;
  riskThesis: ManagementRiskThesis;
  supportingMetrics: Record<string, number | string>;
  mergedTypes: ManagementAttentionCandidateType[];
  concentration?: ManagementAttentionConcentration<T>;
  currentRisk?: number | null;
  dealId?: number | string;
  dealName?: string;
  expectedClose?: Date | string | null;
  indicator?: RiskIndicator;
  movementCategory?: StateOfSalesV2ForecastMovementCategoryKey;
  previousRisk?: number | null;
  urgency?: "high" | "medium" | "low";
};

type RawManagementAttentionCandidate<T extends ManagementAttentionDeal> = Omit<
  ManagementAttentionCandidate<T>,
  "severity"
>;

export type ManagementAttentionInput<T extends ManagementAttentionDeal> = {
  concentrations: ManagementAttentionConcentration<T>[];
  currentSignals: ManagementAttentionSignal<T>[];
  dependencyDeals: T[];
  movement: StateOfSalesV2ForecastMovement | null | undefined;
  selectedPeriodRange?: { from: Date | string; to: Date | string } | null;
  target: number | null;
  totalForecast: number;
  urgentDeals: T[];
};

export type ManagementAttentionShortlist<T extends ManagementAttentionDeal> = {
  candidates: ManagementAttentionCandidate<T>[];
  eligibleCandidates: ManagementAttentionCandidate<T>[];
  selectedCandidates: ManagementAttentionCandidate<T>[];
  suppressedCandidateIds: string[];
};

const severityRank: Record<ManagementAttentionSeverity, number> = {
  critical: 0,
  material: 1,
  informational: 2,
};

const typeActionabilityRank: Record<ManagementAttentionCandidateType, number> = {
  forecast_dependency: 0,
  worsening_outlook: 1,
  organisational_concentration: 2,
  timing_dependency: 3,
  urgent_deal: 4,
  forecast_assumption: 5,
  forecast_divergence: 6,
};

const concentrationDimensionRank: Record<string, number> = {
  stage: 0,
  team: 1,
  sales_rep: 2,
  pipeline: 3,
};

function finiteNumber(value: unknown) {
  const number = Number(value ?? 0);
  return Number.isFinite(number) ? number : 0;
}

function getForecastContribution(deal: ManagementAttentionDeal) {
  return finiteNumber(deal.forecast_contribution);
}

function getForecastShare(deal: ManagementAttentionDeal, totalForecast: number) {
  const suppliedShare = Number(deal.forecast_share);
  if (Number.isFinite(suppliedShare) && suppliedShare >= 0) return suppliedShare;
  return totalForecast > 0 ? getForecastContribution(deal) / totalForecast : 0;
}

function getSeverityFromShare(
  share: number,
  materialThreshold: number,
  criticalThreshold: number,
): ManagementAttentionSeverity {
  if (share >= criticalThreshold) return "critical";
  if (share >= materialThreshold) return "material";
  return "informational";
}

function classifyCandidate<T extends ManagementAttentionDeal>(
  candidate: RawManagementAttentionCandidate<T>,
): ManagementAttentionCandidate<T> {
  const share = candidate.shareOfForecast ?? 0;
  let severity: ManagementAttentionSeverity = "informational";

  if (candidate.type === "forecast_dependency") {
    severity = getSeverityFromShare(
      share,
      FORECAST_MANAGEMENT_ATTENTION_CONFIG.dependency.materialShareOfForecast,
      FORECAST_MANAGEMENT_ATTENTION_CONFIG.dependency.criticalShareOfForecast,
    );
  } else if (candidate.type === "urgent_deal") {
    severity = candidate.urgency === FORECAST_MANAGEMENT_ATTENTION_CONFIG.urgentDeal.eligibleUrgencyBucket
      ? getSeverityFromShare(
        share,
        FORECAST_MANAGEMENT_ATTENTION_CONFIG.urgentDeal.materialShareOfForecast,
        FORECAST_MANAGEMENT_ATTENTION_CONFIG.urgentDeal.criticalShareOfForecast,
      )
      : "informational";
  } else if (candidate.type === "organisational_concentration") {
    const hasSupportedThesis = candidate.supportingMetrics.supportedThesis !== "none";
    severity = hasSupportedThesis
      && (candidate.concentration?.dealCount ?? 0)
        >= FORECAST_MANAGEMENT_ATTENTION_CONFIG.concentration.minimumDeals
      ? getSeverityFromShare(
        share,
        FORECAST_MANAGEMENT_ATTENTION_CONFIG.concentration.materialShareOfForecast,
        FORECAST_MANAGEMENT_ATTENTION_CONFIG.concentration.criticalShareOfForecast,
      )
      : "informational";
  } else if (candidate.type === "worsening_outlook") {
    severity = getSeverityFromShare(
      share,
      FORECAST_MANAGEMENT_ATTENTION_CONFIG.worseningOutlook.materialShareOfForecast,
      FORECAST_MANAGEMENT_ATTENTION_CONFIG.worseningOutlook.criticalShareOfForecast,
    );
  } else if (candidate.type === "timing_dependency") {
    severity = getSeverityFromShare(
      share,
      FORECAST_MANAGEMENT_ATTENTION_CONFIG.timingDependency.materialShareOfForecast,
      FORECAST_MANAGEMENT_ATTENTION_CONFIG.timingDependency.criticalShareOfForecast,
    );
  } else if (candidate.type === "forecast_assumption") {
    const isRiskIncrease = candidate.supportingMetrics.assumptionKind === "risk_increase";
    if (isRiskIncrease) {
      const riskChange = candidate.riskChange ?? 0;
      if (
        share >= FORECAST_MANAGEMENT_ATTENTION_CONFIG.forecastAssumption.materialRiskIncreaseDealShare
        && riskChange >= FORECAST_MANAGEMENT_ATTENTION_CONFIG.forecastAssumption.criticalRiskIncreasePoints
      ) {
        severity = "critical";
      } else if (
        share >= FORECAST_MANAGEMENT_ATTENTION_CONFIG.forecastAssumption.materialRiskIncreaseDealShare
        && riskChange >= FORECAST_MANAGEMENT_ATTENTION_CONFIG.forecastAssumption.materialRiskIncreasePoints
      ) {
        severity = "material";
      }
    } else if (candidate.supportingMetrics.isDiscriminative === 1) {
      severity = getSeverityFromShare(
        share,
        FORECAST_MANAGEMENT_ATTENTION_CONFIG.forecastAssumption.materialShareOfForecast,
        FORECAST_MANAGEMENT_ATTENTION_CONFIG.forecastAssumption.criticalShareOfForecast,
      );
    }
  }

  return { ...candidate, severity };
}

function getDominantDeal<T extends ManagementAttentionDeal>(deals: T[]) {
  return deals
    .toSorted((a, b) => getForecastContribution(b) - getForecastContribution(a))
    .at(0) ?? null;
}

function getMovementDealMap(movement: StateOfSalesV2ForecastMovement | null | undefined) {
  const rows = movement ? Object.values(movement.dealsByCategory).flat() : [];
  return new Map(rows.map(deal => [String(deal.id), deal]));
}

function getIsoDate(value: Date | string | null | undefined) {
  if (!value) return undefined;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? undefined : date.toISOString();
}

function getRiskChange(
  previousRisk: number | null | undefined,
  currentRisk: number | null | undefined,
) {
  const previous = Number(previousRisk);
  const current = Number(currentRisk);
  if (!Number.isFinite(previous) || !Number.isFinite(current) || current <= previous) {
    return undefined;
  }
  return current - previous;
}

function getChangedIndicators(movementDeal: StateOfSalesV2ForecastMovementDeal) {
  return Array.from(new Set(movementDeal.changedIndicators.map(change => change.label)));
}

export function generateManagementAttentionCandidates<T extends ManagementAttentionDeal>({
  concentrations,
  currentSignals,
  dependencyDeals,
  movement,
  selectedPeriodRange,
  target,
  totalForecast,
  urgentDeals,
}: ManagementAttentionInput<T>): ManagementAttentionCandidate<T>[] {
  const movementDealMap = getMovementDealMap(movement);
  const dependencyDealIds = new Set(dependencyDeals.map(deal => String(deal.id)));
  const rawCandidates: RawManagementAttentionCandidate<T>[] = [];

  for (const deal of dependencyDeals) {
    const dealId = String(deal.id);
    const movementDeal = movementDealMap.get(dealId);
    const forecastImpact = getForecastContribution(deal);
    const shareOfForecast = getForecastShare(deal, totalForecast);
    const forecastWithoutEntity = Math.max(0, totalForecast - forecastImpact);
    const riskChange = getRiskChange(movementDeal?.previousRisk, movementDeal?.currentRisk);
    rawCandidates.push({
      id: `dependency:${dealId}`,
      type: "forecast_dependency",
      entityType: "deal",
      entityId: dealId,
      dealIds: [dealId],
      forecastImpact,
      shareOfForecast,
      rootCauseKey: `deal:${dealId}`,
      riskThesis: {
        primaryRisk: "target_dependency",
        consequence: {
          forecastImpact,
          forecastWithoutEntity,
          resultingGapToTarget: target === null ? undefined : forecastWithoutEntity - target,
          shareOfForecast,
        },
        evidence: {
          changedIndicators: movementDeal ? getChangedIndicators(movementDeal) : undefined,
          dealIds: [dealId],
          expectedClose: getIsoDate(deal.adjustedCloseDate),
          riskChange,
        },
      },
      supportingMetrics: {},
      mergedTypes: ["forecast_dependency"],
      currentRisk: movementDeal?.currentRisk ?? null,
      dealId: deal.id,
      dealName: deal.name ?? "Unnamed deal",
      expectedClose: deal.adjustedCloseDate,
      previousRisk: movementDeal?.previousRisk ?? null,
      riskChange,
    });
  }

  for (const deal of urgentDeals) {
    const dealId = String(deal.id);
    const forecastImpact = getForecastContribution(deal);
    const shareOfForecast = getForecastShare(deal, totalForecast);
    rawCandidates.push({
      id: `urgent:${dealId}`,
      type: "urgent_deal",
      entityType: "deal",
      entityId: dealId,
      dealIds: [dealId],
      forecastImpact,
      shareOfForecast,
      urgencyScore: finiteNumber(deal.urgency_score),
      rootCauseKey: `deal:${dealId}`,
      riskThesis: {
        primaryRisk: "timing_dependency",
        consequence: { forecastImpact, shareOfForecast },
        evidence: {
          dealIds: [dealId],
          expectedClose: getIsoDate(deal.adjustedCloseDate),
        },
      },
      supportingMetrics: { closeWindowDays: 60 },
      mergedTypes: ["urgent_deal"],
      dealId: deal.id,
      dealName: deal.name ?? "Unnamed deal",
      expectedClose: deal.adjustedCloseDate,
      currentRisk: deal.weighted_risk_score ?? deal.risk_score ?? null,
      urgency: deal.urgency_bucket,
    });
  }

  for (const concentration of concentrations) {
    const dealIds = concentration.deals.map(deal => String(deal.id)).toSorted();
    const dominantDeal = getDominantDeal(concentration.deals);
    const dominantDealShare = dominantDeal && concentration.value > 0
      ? getForecastContribution(dominantDeal) / concentration.value
      : 0;
    const supportsSmallStageCohort = concentration.key === "stage"
      && concentration.dealCount <= FORECAST_MANAGEMENT_ATTENTION_CONFIG
        .concentration.maximumDealsForStageDependencyThesis;
    rawCandidates.push({
      id: `concentration:${concentration.key}:${concentration.label}`,
      type: "organisational_concentration",
      entityType: concentration.key === "sales_rep" ? "rep" : concentration.key,
      entityId: concentration.label,
      dealIds,
      forecastImpact: concentration.value,
      shareOfForecast: concentration.share,
      rootCauseKey: `concentration:${concentration.key}:${concentration.label}`,
      riskThesis: {
        primaryRisk: "target_dependency",
        consequence: {
          forecastImpact: concentration.value,
          shareOfForecast: concentration.share,
        },
        evidence: { dealIds },
      },
      supportingMetrics: {
        dealCount: concentration.dealCount,
        dominantDealId: dominantDeal ? String(dominantDeal.id) : "",
        dominantDealShare,
        supportedThesis: supportsSmallStageCohort ? "small_stage_cohort" : "none",
      },
      mergedTypes: ["organisational_concentration"],
      concentration,
    });
  }

  const weakenedOutlook = movement?.available
    ? Math.abs(finiteNumber(movement.values.weakenedOutlook))
    : 0;
  const weakenedDeals = movement?.dealsByCategory.weakened_outlook ?? [];
  if (weakenedOutlook > 0 && weakenedDeals.length > 0) {
    const largestDeal = weakenedDeals
      .toSorted((a, b) => Math.abs(b.forecastImpact) - Math.abs(a.forecastImpact))
      .at(0) ?? null;
    const largestDealShare = largestDeal
      ? Math.abs(largestDeal.forecastImpact) / weakenedOutlook
      : 0;
    const rootCauseKey = largestDeal
      && largestDealShare >= FORECAST_MANAGEMENT_ATTENTION_CONFIG.concentration.suppressWhenOneDealExplainsShare
      ? `deal:${largestDeal.id}`
      : "movement:weakened_outlook";
    const totalNegativeMovement = Object.values(movement?.values ?? {}).reduce(
      (sum, value) => sum + (finiteNumber(value) < 0 ? Math.abs(finiteNumber(value)) : 0),
      0,
    );
    const riskChange = getRiskChange(largestDeal?.previousRisk, largestDeal?.currentRisk);
    const changedIndicators = Array.from(new Set(
      weakenedDeals.flatMap(deal => deal.changedIndicators.map(change => change.label)),
    ));
    rawCandidates.push({
      id: "movement:weakened_outlook",
      type: "worsening_outlook",
      entityType: largestDeal && weakenedDeals.length === 1 ? "deal" : "forecast",
      entityId: largestDeal && weakenedDeals.length === 1 ? String(largestDeal.id) : undefined,
      dealIds: weakenedDeals.map(deal => String(deal.id)).toSorted(),
      forecastImpact: weakenedOutlook,
      shareOfForecast: totalForecast > 0 ? weakenedOutlook / totalForecast : 0,
      movementImpact: weakenedOutlook,
      rootCauseKey,
      riskThesis: {
        primaryRisk: "negative_movement",
        consequence: {
          forecastImpact: weakenedOutlook,
          movementImpact: weakenedOutlook,
          shareOfForecast: totalForecast > 0 ? weakenedOutlook / totalForecast : 0,
        },
        evidence: {
          changedIndicators,
          dealIds: weakenedDeals.map(deal => String(deal.id)).toSorted(),
          riskChange,
        },
      },
      supportingMetrics: {
        affectedDealCount: weakenedDeals.length,
        dealNames: weakenedDeals.map(deal => deal.name).join("|"),
        largestDealId: largestDeal ? String(largestDeal.id) : "",
        largestDealImpact: largestDeal ? Math.abs(largestDeal.forecastImpact) : 0,
        largestDealImpactShare: largestDealShare,
        shareOfNegativeMovement: totalNegativeMovement > 0
          ? weakenedOutlook / totalNegativeMovement
          : 0,
      },
      mergedTypes: ["worsening_outlook"],
      movementCategory: "weakened_outlook",
    });
  }

  if (selectedPeriodRange && totalForecast > 0) {
    const periodStart = new Date(selectedPeriodRange.from);
    const periodEnd = new Date(selectedPeriodRange.to);
    if (!Number.isNaN(periodStart.getTime()) && !Number.isNaN(periodEnd.getTime())) {
      const finalWindowStart = subDays(
        periodEnd,
        FORECAST_MANAGEMENT_ATTENTION_CONFIG.timingDependency.finalPeriodDays - 1,
      );
      const timingDeals = dependencyDeals.filter((deal) => {
        if (!deal.adjustedCloseDate) return false;
        const expectedClose = new Date(deal.adjustedCloseDate);
        if (Number.isNaN(expectedClose.getTime())) return false;
        return isWithinInterval(expectedClose, {
          start: finalWindowStart > periodStart ? finalWindowStart : periodStart,
          end: periodEnd,
        });
      });
      const timingValue = timingDeals.reduce(
        (sum, deal) => sum + getForecastContribution(deal),
        0,
      );
      if (timingDeals.length > 0 && timingValue > 0) {
        rawCandidates.push({
          id: "timing:period_end",
          type: "timing_dependency",
          entityType: "forecast",
          dealIds: timingDeals.map(deal => String(deal.id)).toSorted(),
          forecastImpact: timingValue,
          shareOfForecast: timingValue / totalForecast,
          rootCauseKey: "timing:period_end",
          riskThesis: {
            primaryRisk: "timing_dependency",
            consequence: {
              forecastImpact: timingValue,
              shareOfForecast: timingValue / totalForecast,
            },
            evidence: {
              dealIds: timingDeals.map(deal => String(deal.id)).toSorted(),
            },
          },
          supportingMetrics: {
            dealCount: timingDeals.length,
            finalPeriodDays: FORECAST_MANAGEMENT_ATTENTION_CONFIG.timingDependency.finalPeriodDays,
          },
          mergedTypes: ["timing_dependency"],
        });
      }
    }
  }

  for (const signal of currentSignals) {
    const discriminativeEvidence = signal.discriminativeEvidence;
    const dominantDeal = getDominantDeal(signal.deals);
    const dominantDealShare = dominantDeal && signal.forecastValue > 0
      ? getForecastContribution(dominantDeal) / signal.forecastValue
      : 0;
    const rootCauseKey = dominantDeal
      && dominantDealShare >= FORECAST_MANAGEMENT_ATTENTION_CONFIG.concentration.suppressWhenOneDealExplainsShare
      ? `deal:${dominantDeal.id}`
      : `assumption:${signal.indicator}`;
    rawCandidates.push({
      id: `assumption:${signal.indicator}`,
      type: "forecast_assumption",
      entityType: dominantDeal && signal.deals.length === 1 ? "deal" : "forecast",
      entityId: signal.indicator,
      dealIds: signal.deals.map(deal => String(deal.id)).toSorted(),
      forecastImpact: signal.forecastValue,
      shareOfForecast: signal.forecastShare ?? 0,
      rootCauseKey,
      riskThesis: {
        primaryRisk: "signal_deterioration",
        consequence: {
          forecastImpact: signal.forecastValue,
          movementImpact: discriminativeEvidence?.movementImpact,
          shareOfForecast: signal.forecastShare ?? 0,
        },
        evidence: {
          dealIds: signal.deals.map(deal => String(deal.id)).toSorted(),
        },
      },
      supportingMetrics: {
        affectedDealCount: signal.affectedDealCount,
        assumptionKind: "current_signal",
        dominantDealId: dominantDeal ? String(dominantDeal.id) : "",
        dominantDealShare,
        comparisonRatio: discriminativeEvidence?.comparisonRatio ?? 0,
        discriminativeEvidenceKind: discriminativeEvidence?.kind ?? "none",
        isDiscriminative: discriminativeEvidence ? 1 : 0,
        signalLabel: signal.label,
      },
      mergedTypes: ["forecast_assumption"],
      indicator: signal.indicator,
    });
  }

  for (const movementDeal of movementDealMap.values()) {
    if (!dependencyDealIds.has(String(movementDeal.id))) continue;
    if (movementDeal.previousRisk === null || movementDeal.currentRisk === null) continue;
    const riskChange = movementDeal.currentRisk - movementDeal.previousRisk;
    if (riskChange <= 0 || (movementDeal.currentForecastContribution ?? 0) <= 0) continue;
    const share = totalForecast > 0
      ? (movementDeal.currentForecastContribution ?? 0) / totalForecast
      : 0;
    rawCandidates.push({
      id: `assumption:risk-increase:${movementDeal.id}`,
      type: "forecast_assumption",
      entityType: "deal",
      entityId: String(movementDeal.id),
      dealIds: [String(movementDeal.id)],
      forecastImpact: movementDeal.currentForecastContribution ?? 0,
      shareOfForecast: share,
      riskChange,
      rootCauseKey: `deal:${movementDeal.id}`,
      riskThesis: {
        primaryRisk: "signal_deterioration",
        consequence: {
          forecastImpact: movementDeal.currentForecastContribution ?? 0,
          shareOfForecast: share,
        },
        evidence: {
          changedIndicators: getChangedIndicators(movementDeal),
          dealIds: [String(movementDeal.id)],
          riskChange,
        },
      },
      supportingMetrics: {
        assumptionKind: "risk_increase",
        currentRisk: movementDeal.currentRisk,
        previousRisk: movementDeal.previousRisk,
      },
      mergedTypes: ["forecast_assumption"],
      currentRisk: movementDeal.currentRisk,
      dealId: movementDeal.id,
      dealName: movementDeal.name,
      previousRisk: movementDeal.previousRisk,
    });
  }

  return rawCandidates.map(classifyCandidate);
}

function mergeSameRootCause<T extends ManagementAttentionDeal>(
  candidates: ManagementAttentionCandidate<T>[],
) {
  const grouped = new Map<string, ManagementAttentionCandidate<T>>();

  for (const candidate of candidates.toSorted((a, b) => (
    typeActionabilityRank[a.type] - typeActionabilityRank[b.type]
    || severityRank[a.severity] - severityRank[b.severity]
  ))) {
    const existing = grouped.get(candidate.rootCauseKey);
    if (!existing) {
      grouped.set(candidate.rootCauseKey, candidate);
      continue;
    }

    const primary = typeActionabilityRank[candidate.type] < typeActionabilityRank[existing.type]
      ? candidate
      : existing;
    const secondary = primary === existing ? candidate : existing;
    const severity = severityRank[existing.severity] <= severityRank[candidate.severity]
      ? existing.severity
      : candidate.severity;
    const changedIndicators = Array.from(new Set([
      ...(primary.riskThesis.evidence.changedIndicators ?? []),
      ...(secondary.riskThesis.evidence.changedIndicators ?? []),
    ]));
    grouped.set(candidate.rootCauseKey, {
      ...primary,
      severity,
      dealIds: Array.from(new Set([...existing.dealIds, ...candidate.dealIds])).toSorted(),
      forecastImpact: Math.max(existing.forecastImpact ?? 0, candidate.forecastImpact ?? 0),
      movementImpact: Math.max(existing.movementImpact ?? 0, candidate.movementImpact ?? 0) || undefined,
      riskChange: Math.max(existing.riskChange ?? 0, candidate.riskChange ?? 0) || undefined,
      urgencyScore: Math.max(existing.urgencyScore ?? 0, candidate.urgencyScore ?? 0) || undefined,
      riskThesis: {
        primaryRisk: primary.riskThesis.primaryRisk,
        consequence: {
          ...secondary.riskThesis.consequence,
          ...primary.riskThesis.consequence,
          forecastImpact: Math.max(
            primary.riskThesis.consequence.forecastImpact ?? 0,
            secondary.riskThesis.consequence.forecastImpact ?? 0,
          ) || undefined,
          movementImpact: Math.max(
            primary.riskThesis.consequence.movementImpact ?? 0,
            secondary.riskThesis.consequence.movementImpact ?? 0,
          ) || undefined,
        },
        evidence: {
          ...secondary.riskThesis.evidence,
          ...primary.riskThesis.evidence,
          changedIndicators: changedIndicators.length ? changedIndicators : undefined,
          dealIds: Array.from(new Set([
            ...primary.riskThesis.evidence.dealIds,
            ...secondary.riskThesis.evidence.dealIds,
          ])).toSorted(),
          riskChange: Math.max(
            primary.riskThesis.evidence.riskChange ?? 0,
            secondary.riskThesis.evidence.riskChange ?? 0,
          ) || undefined,
        },
      },
      supportingMetrics: {
        ...secondary.supportingMetrics,
        ...primary.supportingMetrics,
      },
      mergedTypes: Array.from(new Set([
        ...existing.mergedTypes,
        ...candidate.mergedTypes,
      ])),
      currentRisk: primary.currentRisk ?? secondary.currentRisk,
      expectedClose: primary.expectedClose ?? secondary.expectedClose,
      previousRisk: primary.previousRisk ?? secondary.previousRisk,
      urgency: primary.urgency ?? secondary.urgency,
    });
  }

  return Array.from(grouped.values());
}

function getFinancialImpact(candidate: ManagementAttentionCandidate) {
  return Math.max(candidate.forecastImpact ?? 0, candidate.movementImpact ?? 0);
}

function getRiskThesisRank(candidate: ManagementAttentionCandidate) {
  const resultingGap = candidate.riskThesis.consequence.resultingGapToTarget;
  if (resultingGap !== undefined && resultingGap < 0) return 0;
  if (candidate.riskThesis.primaryRisk === "negative_movement") return 1;
  if (
    candidate.riskThesis.primaryRisk === "conversion_risk"
    || candidate.riskThesis.primaryRisk === "progression_risk"
  ) return 2;
  if (candidate.riskThesis.primaryRisk === "timing_dependency") return 3;
  if (candidate.riskThesis.primaryRisk === "target_dependency") return 4;
  if (candidate.riskThesis.primaryRisk === "signal_deterioration") return 5;
  return 6;
}

export function deduplicateManagementAttentionCandidates<T extends ManagementAttentionDeal>(
  candidates: ManagementAttentionCandidate<T>[],
) {
  const merged = mergeSameRootCause(candidates);
  const suppressedCandidateIds: string[] = [];
  const exactConcentrationSets = new Map<string, ManagementAttentionCandidate<T>>();
  const withoutDuplicateConcentrations: ManagementAttentionCandidate<T>[] = [];

  for (const candidate of merged) {
    if (candidate.type !== "organisational_concentration" || !candidate.concentration) {
      withoutDuplicateConcentrations.push(candidate);
      continue;
    }
    const setKey = candidate.dealIds.join("|");
    const existing = exactConcentrationSets.get(setKey);
    if (!existing) {
      exactConcentrationSets.set(setKey, candidate);
      withoutDuplicateConcentrations.push(candidate);
      continue;
    }
    const existingRank = concentrationDimensionRank[existing.concentration?.key ?? ""] ?? 99;
    const candidateRank = concentrationDimensionRank[candidate.concentration.key] ?? 99;
    if (candidateRank < existingRank) {
      suppressedCandidateIds.push(existing.id);
      const index = withoutDuplicateConcentrations.findIndex(row => row.id === existing.id);
      if (index >= 0) withoutDuplicateConcentrations[index] = candidate;
      exactConcentrationSets.set(setKey, candidate);
    } else {
      suppressedCandidateIds.push(candidate.id);
    }
  }

  const materialDealRoots = new Set(
    withoutDuplicateConcentrations
      .filter(candidate => candidate.type !== "organisational_concentration")
      .filter(candidate => candidate.rootCauseKey.startsWith("deal:"))
      .map(candidate => candidate.rootCauseKey.slice("deal:".length)),
  );
  const deduplicated = withoutDuplicateConcentrations.filter((candidate) => {
    if (candidate.type !== "organisational_concentration") return true;
    const dominantDealId = String(candidate.supportingMetrics.dominantDealId ?? "");
    const dominantDealShare = finiteNumber(candidate.supportingMetrics.dominantDealShare);
    const isDuplicate = materialDealRoots.has(dominantDealId)
      && dominantDealShare
        >= FORECAST_MANAGEMENT_ATTENTION_CONFIG.concentration.suppressWhenOneDealExplainsShare;
    if (isDuplicate) suppressedCandidateIds.push(candidate.id);
    return !isDuplicate;
  });

  return { candidates: deduplicated, suppressedCandidateIds };
}

export function selectManagementAttentionCandidates<T extends ManagementAttentionDeal>(
  candidates: ManagementAttentionCandidate<T>[],
) {
  return candidates
    .filter(candidate => candidate.severity !== "informational")
    .toSorted((a, b) => (
      getRiskThesisRank(a) - getRiskThesisRank(b)
      || severityRank[a.severity] - severityRank[b.severity]
      || getFinancialImpact(b) - getFinancialImpact(a)
      || typeActionabilityRank[a.type] - typeActionabilityRank[b.type]
      || a.id.localeCompare(b.id)
    ))
    .slice(0, FORECAST_MANAGEMENT_ATTENTION_CONFIG.maxPriorities);
}

export function buildManagementAttentionShortlist<T extends ManagementAttentionDeal>(
  input: ManagementAttentionInput<T>,
): ManagementAttentionShortlist<T> {
  const candidates = generateManagementAttentionCandidates(input);
  const eligibleCandidates = candidates.filter(candidate => candidate.severity !== "informational");
  const deduplicated = deduplicateManagementAttentionCandidates(eligibleCandidates);
  const selectedCandidates = selectManagementAttentionCandidates(deduplicated.candidates);

  return {
    candidates,
    eligibleCandidates,
    selectedCandidates,
    suppressedCandidateIds: deduplicated.suppressedCandidateIds,
  };
}
